import { useContext, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import {
  TeacherEvaluationPageContext,
  filteredStudentsSelector,
  TeacherEvaluationPageActions as actions,
} from '../../context';

import {
  getAssignmentMediaTypeHint,
  getMediaFileInfoFromUrl,
  pickPreviewMediaUrl,
  pickPreviewMediaUrlAsync,
  type MediaWithUrl,
} from '../../../../utils/media';
import { useStoreContext } from '../../../../factorys/useStoreContext';
import { IdentityContext } from '../../../App/identity.context';
import { SubmissionStatus } from '../../../../services/graphql/types/graphql';
import { AssignmentMediaPreview } from '../../../../components/AssignmentMediaPreview';

const PreviewImage = ({
  url,
  className,
  grade,
}: {
  url: string;
  className: string;
  grade: string;
  status: SubmissionStatus;
}) => {
  if (!url) {
    return <div className={cx('h-full w-full', className)} />;
  }

  const { type, ext } = getMediaFileInfoFromUrl(url);

  if (type === 'pdf' || ext === 'pdf') {
    return (
      <div className={cx('relative h-full w-full', className)}>
        <div className="flex h-full w-full items-center justify-center text-red-500">PDF</div>
        <div className="absolute bottom-0 right-0 flex h-9 w-9 items-center justify-center rounded-ss-[999px] bg-red-600 text-white">
          {grade}
        </div>
      </div>
    );
  }

  const mediaTypeHint = getAssignmentMediaTypeHint(url, 'thumbnail');

  return (
    <div className={cx('relative h-full w-full', className)}>
      <AssignmentMediaPreview url={url} thumbnail mediaTypeHint={mediaTypeHint} />
      <div className="absolute bottom-0 right-0 flex h-9 w-9 items-center justify-center rounded-ss-[999px] bg-red-600 text-white">
        {grade}
      </div>
    </div>
  );
};

const StudentListItem = ({
  medias,
  studentNumber,
  userName,
  grade,
  status,
  isActive,
  onSelect,
}: {
  medias: MediaWithUrl[];
  studentNumber: string;
  userName: string;
  grade: string;
  status: SubmissionStatus;
  isActive: boolean;
  onSelect: () => void;
}) => {
  const [previewUrl, setPreviewUrl] = useState(() => pickPreviewMediaUrl(medias));
  const mediaKey = useMemo(
    () => medias.map((m) => m.mediaId ?? m.url).join('|'),
    [medias],
  );

  useEffect(() => {
    let cancelled = false;
    pickPreviewMediaUrlAsync(medias).then((url) => {
      if (!cancelled) {
        setPreviewUrl(url);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [mediaKey, medias]);

  return (
    <div
      className={cx(
        'w-[110px] h-[110px] bg-black relative flex-shrink-0 overflow-hidden cursor-pointer',
      )}
      onClick={onSelect}
    >
      <PreviewImage
        className={cx('brightness-[0.3]', isActive && '!brightness-100')}
        url={previewUrl}
        grade={grade}
        status={status}
      />
      <div className="absolute inset-0 z-10 pointer-events-none text-white p-1 pb-8 bg-[linear-gradient(180deg,#1a1a1a_35%,rgba(0,0,0,0.00)_100%)]">
        <div className="text-xs">{studentNumber}</div>
        <div className="text-base">{userName}</div>
      </div>
    </div>
  );
};

export const StudentSelector = () => {
  const {
    state,
    state: { activeStudentIndex },
    dispatch,
  } = useContext(TeacherEvaluationPageContext);
  const { currentIdentity } = useStoreContext(IdentityContext);
  const filteredStudent = useMemo(
    () => filteredStudentsSelector(state, currentIdentity?.identity),
    [currentIdentity, state.submissionFilter, state.sortBy, state.assignmentData],
  );

  return (
    <div className="border-l border-r space-y-[5px] flex flex-col px-[5px] overflow-y-auto">
      {filteredStudent.length === 0 && state.submissionFilter !== 'all' ? (
        <div className="w-[110px] py-8 text-center text-white/60 text-xs leading-5">
          {state.submissionFilter === 'pending' ? '暂无待评价' : '暂无星标'}
        </div>
      ) : null}
      {filteredStudent.map((s, iStudent: number) => {
        const medias: MediaWithUrl[] =
          s.submissions?.flatMap((submission) =>
            (submission.artworks ?? []).flatMap((artwork) => artwork.medias ?? []),
          ) ?? [];

        return (
          <StudentListItem
            key={`student-list-${s.user.userId}`}
            medias={medias}
            studentNumber={s.studentNumber}
            userName={s.user.name}
            grade={s.submissions?.[0]?.calculatedGrade?.toString() ?? ''}
            status={s.submissions?.[0]?.submissionStatus}
            isActive={iStudent === activeStudentIndex}
            onSelect={() => dispatch(actions.setActiveStudentIndex(iStudent))}
          />
        );
      })}
    </div>
  );
};
